/**
 * SVG-session parameterization. An SVG source carries `{{key}}` tokens in
 * attribute values and text content; rendering substitutes each token with
 * the matching field value, XML-escaped. There is no conditional logic —
 * this is a straight, escaped find-and-replace.
 *
 * Tokens may carry an optional dotted property (`{{key.fontFamily}}`) to pull
 * a derived attribute out of a text-rich value. See applySvgFields for the
 * resolution table. Platform policy: SVG text fields are text-rich only, so a
 * template references `{{title.text}}` / `{{title.fontFamily}}` / etc. rather
 * than a bare `{{title}}`.
 */

import type { RichTextValue } from "../types/template";
import { SUPPORTED_FONTS } from "./fonts";

/** XML-escape a string for safe insertion into SVG attribute values / text.
 *  `&` MUST be escaped first, otherwise the ampersands introduced by the
 *  later replacements would themselves be double-escaped. */
export function xmlEscape(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/** XML-escape but PRESERVE apostrophes. Used for the derived `fontFamily`
 *  stack, whose CSS-style single quotes (`'Rubik', system-ui, …`) are
 *  structural and valid unescaped inside a double-quoted attribute. Escaping
 *  them to `&apos;` would produce a malformed font-family list. */
function xmlEscapeKeepApos(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Per-category generic fallback appended after the primary family in a
 *  `fontFamily` stack, so a font-load failure still degrades to a sane
 *  system face. Mirrors the categories in lib/fonts.ts SUPPORTED_FONTS. */
const CATEGORY_FALLBACK: Record<string, string> = {
  sans: "system-ui, sans-serif",
  mono: "ui-monospace, monospace",
  serif: "serif",
  display: "system-ui, sans-serif",
};

/** Build a CSS-style font-family stack for a registry family: the family
 *  quoted first, then its category's generic fallback. Unknown families still
 *  lead with the requested family, falling back to sans. */
function fontStack(family: string): string {
  const entry = SUPPORTED_FONTS.find((f) => f.family === family);
  const fallback =
    (entry && CATEGORY_FALLBACK[entry.category]) ?? "system-ui, sans-serif";
  return `'${family}', ${fallback}`;
}

/** True when a value is a text-rich object (has a string `.text`). */
function isRichValue(v: unknown): v is RichTextValue {
  return (
    v !== null &&
    typeof v === "object" &&
    typeof (v as { text?: unknown }).text === "string"
  );
}

/**
 * Matches `{{ key }}` or `{{ key.prop }}` tokens. `key` is [a-zA-Z0-9_]; the
 * optional dotted `prop` is [a-zA-Z] (fontFamily, fontWeight, text, …). Inner
 * whitespace around the whole expression is tolerated.
 */
const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)(?:\.([a-zA-Z]+))?\s*\}\}/g;

/**
 * Replace every `{{key}}` / `{{key.prop}}` occurrence in `source` with the
 * XML-escaped string form of the resolved value. Tokens with no matching key
 * are left as-is (so an in-progress edit doesn't lose text).
 *
 * Resolution for `v = values[key]`:
 *  - No dot: String(v), XML-escaped; null/undefined → "". If `v` is a rich
 *    object, a bare `{{key}}` resolves to `v.text` (defensive — policy forbids
 *    bare tokens on rich fields, but we never want to render "[object Object]").
 *  - Dotted on a rich object `{text, font, fontSize, bold?, italic?, underline?}`:
 *      text           → v.text
 *      fontSize       → String(v.fontSize)
 *      fontFamily     → `'<font>', <category fallback>`
 *      fontWeight     → v.bold ? "700" : "400"
 *      fontStyle      → v.italic ? "italic" : "normal"
 *      textDecoration → v.underline ? "underline" : "none"
 *      (anything else → "")
 *  - Dotted on a non-rich value: `text` behaves like bare; anything else → "".
 */
/** Remove `<g data-layer="KEY">…</g>` subtrees for every value that is
 *  exactly `false` (layer fields toggled off; missing/true = visible).
 *  Balanced against nested `<g>` groups. Only markup that opts in with a
 *  `data-layer` attribute is ever touched, so ordinary boolean fields and
 *  ordinary groups are unaffected. */
export function stripHiddenLayers(
  source: string,
  values: Record<string, unknown>,
): string {
  let out = source;
  for (const [key, v] of Object.entries(values)) {
    if (v !== false) continue;
    for (;;) {
      const open = out.match(
        new RegExp(`<g\\b[^>]*data-layer=["']${key}["'][^>]*>`),
      );
      if (!open || open.index === undefined) break;
      const start = open.index;
      if (open[0].endsWith("/>")) {
        out = out.slice(0, start) + out.slice(start + open[0].length);
        continue;
      }
      // Walk <g …> / </g> tags to find the matching close for this group.
      const tagRe = /<g\b[^>]*>|<\/g>/g;
      tagRe.lastIndex = start + open[0].length;
      let depth = 1;
      let end = -1;
      for (let m = tagRe.exec(out); m; m = tagRe.exec(out)) {
        depth += m[0] === "</g>" ? -1 : m[0].endsWith("/>") ? 0 : 1;
        if (depth === 0) {
          end = m.index + m[0].length;
          break;
        }
      }
      if (end === -1) break; // unbalanced markup — leave untouched
      out = out.slice(0, start) + out.slice(end);
    }
  }
  return out;
}

/**
 * Multi-line text in SVG: a `\n` inside a rich value's text is invisible in
 * `<text>` (SVG has no automatic line breaking), so expand the standard
 * authoring shape — `{{key.text}}` as the SOLE content of a `<text>`
 * element — into stacked `<tspan>` lines. Each line repeats the parent's
 * `x` (so text-anchor keeps working) and steps down `dy="1.25em"`, which
 * resolves against the element's own font-size. Tokens in any other
 * position fall through to the flat substitution below, where newlines
 * degrade to spaces.
 */
function expandMultilineText(
  source: string,
  values: Record<string, unknown>,
): string {
  return source.replace(
    /(<text\b[^>]*>)\s*\{\{([a-zA-Z][a-zA-Z0-9_-]*)\.text\}\}\s*(<\/text>)/g,
    (match, open: string, key: string, close: string) => {
      const value = values[key];
      if (!isRichValue(value) || !value.text.includes("\n")) return match;
      const x = /\bx\s*=\s*["']([^"']*)["']/.exec(open)?.[1] ?? "0";
      const lines = value.text.split("\n");
      const tspans = lines
        .map(
          (line, i) =>
            `<tspan x="${xmlEscape(x)}"${i === 0 ? "" : ' dy="1.25em"'}>${xmlEscape(line) || " "}</tspan>`,
        )
        .join("");
      return `${open}${tspans}${close}`;
    },
  );
}

export function applySvgFields(
  source: string,
  values: Record<string, unknown>,
): string {
  source = stripHiddenLayers(source, values);
  source = expandMultilineText(source, values);
  return source.replace(TOKEN_RE, (match, key: string, prop?: string) => {
    if (!Object.prototype.hasOwnProperty.call(values, key)) return match;
    const value = values[key];

    // Bare token (no dotted property).
    if (!prop) {
      if (value === null || value === undefined) return "";
      // Newlines survive only via tspan expansion above; anywhere else
      // (attributes, mixed content) they'd be invisible or invalid.
      if (isRichValue(value)) return xmlEscape(value.text.replace(/\n/g, " "));
      return xmlEscape(String(value));
    }

    // Dotted token on a rich value — the derived-attribute table.
    if (isRichValue(value)) {
      switch (prop) {
        case "text":
          return xmlEscape(value.text.replace(/\n/g, " "));
        case "fontSize":
          return xmlEscape(String(value.fontSize));
        case "fontFamily":
          return xmlEscapeKeepApos(fontStack(value.font));
        case "fontWeight":
          return value.bold ? "700" : "400";
        case "fontStyle":
          return value.italic ? "italic" : "normal";
        case "textDecoration":
          return value.underline ? "underline" : "none";
        default:
          return "";
      }
    }

    // Dotted token on a non-rich value: only `.text` degrades to the bare form.
    if (prop === "text") {
      if (value === null || value === undefined) return "";
      return xmlEscape(String(value));
    }
    return "";
  });
}
